import { parseImageRef, isAcrHost } from "./preconditions.js";
import { deriveAcrName, ensureAcr, ensureImageInAcr } from "./acr.js";
import type { FoundryProject } from "./foundry-project.js";

/** Resource-group segment from an ARM account scope id. */
function resourceGroupFromScope(scope: string): string {
  return /\/resourceGroups\/([^/]+)\//.exec(scope)?.[1] ?? "";
}

export interface StageResult {
  image: string; // the ref to deploy (ACR ref when staged; unchanged otherwise)
  staged: boolean;
  notes: string[];
}

/**
 * Post-2026-06-25 Foundry projects reject anonymous image pulls — the platform
 * authenticates to the registry with the project managed identity. So a public
 * image (e.g. ghcr.io) is staged into the per-customer ACR (create-if-missing +
 * server-side import) and the ACR ref is returned. An ACR ref is passed through
 * unchanged (the bring-your-own-registry path).
 */
export async function stagePublicImageIfNeeded(args: {
  image: string;
  project: FoundryProject;
  onProgress?: (m: string) => void;
}): Promise<StageResult> {
  const notes: string[] = [];
  const { host, repo, tag } = parseImageRef(args.image);
  if (isAcrHost(host)) return { image: args.image, staged: false, notes };

  const acrName = deriveAcrName(args.project.accountName);
  const resourceGroup = resourceGroupFromScope(args.project.accountScope);
  args.onProgress?.(`staging ${args.image} into ${acrName} (server-side import)…`);
  const acr = await ensureAcr({ acrName, resourceGroup, location: args.project.region });
  if (acr.created) notes.push(`created ACR '${acrName}' in ${resourceGroup} for hosted-agent images.`);
  const { imported } = await ensureImageInAcr({ acrName, sourceRef: args.image, repo, tag });
  const acrRef = `${acr.loginServer}/${repo}:${tag}`;
  notes.push(
    imported
      ? `imported ${args.image} → ${acrRef} (server-side, no local build).`
      : `image already present in ${acrName}; using ${acrRef}.`,
  );
  return { image: acrRef, staged: true, notes };
}
